import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Header from "../../components/ui/Header";
import AdminToolbar from "../../components/ui/AdminToolbar";
import NotificationCenter from "../../components/ui/NotificationCenter";

import MetricsCard from "./components/MetricsCard";
import UserTable from "./components/UserTable";
import UserFormModal from "./components/UserFormModal";

import {
  createUserService,
  getUsersService,
  updateUserService,
  deleteClientService,
} from "redux/services/users_service";

import { toast } from "react-toastify";
import DeleteConfirmModal from "./components/DeleteConfirmModal";

const UsersManagementHub = () => {
  const dispatch = useDispatch();
  const { usersList, metaUsers } = useSelector((state) => state.users);

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [language, setLanguage] = useState("ar");

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMode, setUserMode] = useState("add");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getUsersService({ page, search: searchQuery }));
  }, [dispatch, page, searchQuery]);

  /* =========================
     Mapping users
  ========================= */
  const mappedUsers = usersList?.map((user) => ({
    id: user.id,
    name: `${user.first_name} ${user.last_name}`,
    phone_number: user.phone_number,
    birthday: user.birthday,
    enc_id: user.enc_id,
    role: user.role == "CUSTOMER" ? "زبون" : "مسؤول",
    status: user.is_verified ? "Active" : "Inactive",
    lastActive: new Date(user.created_at).toLocaleDateString(),
    avatar: `https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}&background=random`,
  }));

  const handleEdit = (userId) => {
    const user = usersList.find((u) => u.id === userId);
    setSelectedUser(user);
    setUserMode("edit");
    setIsUserModalOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteClientService(selectedUser?.enc_id)).then((res) => {
      if (!res.error) {
        toast("تم الحذف بنجاح");
        setIsDeleteModalOpen(false);
        dispatch(getUsersService({ page }));
      }
    });
  };

  const handleExport = () => {
    console.log("Export users");
  };

  return (
    <div dir="rtl" className="min-h-screen bg-background">
      <Header />
      <AdminToolbar currentEntity="users" />

      <div className="fixed top-20 right-4 z-50">
        <NotificationCenter />
      </div>

      <main className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-semibold">
            {language === "en"
              ? "Users Management Hub"
              : "مركز إدارة المستخدمين"}
          </h1>
        </div>

        {/* Search & Actions */}
        <div className="bg-card rounded-xl p-6 shadow border border-border mb-8">
          <div className="flex flex-col lg:flex-row gap-4 justify-between">
            <Input
              type="search"
              placeholder="البحث عن المستخدمين..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="lg:max-w-md"
            />

            <div className="flex gap-2">
              <Button
                variant="outline"
                iconName="Download"
                onClick={handleExport}
              >
                تصدير
              </Button>

              <Button
                variant="default"
                iconName="UserPlus"
                onClick={() => {
                  setUserMode("add");
                  setSelectedUser(null);
                  setIsUserModalOpen(true);
                }}
              >
                إضافة مستخدم
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <UserTable
          users={mappedUsers}
          metaUsers={metaUsers}
          onPageChange={(p) => setPage(p)}
          onEdit={handleEdit}
          onDelete={(user) => {
            setSelectedUser(user);
            setIsDeleteModalOpen(true);
          }}
        />

        {/* User Modal */}
        <UserFormModal
          isOpen={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          user={selectedUser}
          mode={userMode}
          language={language}
          onSave={(formData) => {
            if (userMode === "add") {
              dispatch(createUserService(formData)).then((response) => {
                if (
                  response.payload?.status === 200 ||
                  response.payload?.status === 201
                ) {
                  toast.success("تم الإضافة بنجاح");

                  setIsUserModalOpen(false);
                  dispatch(getUsersService({ page }));
                } else {
                  Object.values(response.payload.response?.data?.errors)
                    .flatMap((errorArray) => errorArray)
                    .forEach((error) => toast.warn(error));
                }
              });
            } else {
              dispatch(
                updateUserService({
                  id: selectedUser.enc_id,
                  data: formData,
                }),
              ).then((response) => {
                if (
                  response.payload?.status === 200 ||
                  response.payload?.status === 201
                ) {
                  toast.success(
                    language === "ar"
                      ? "تم التعديل بنجاح"
                      : "Added successfully",
                  );

                  setIsUserModalOpen(false);
                  dispatch(getUsersService({ page }));
                } else {
                  Object.values(response.payload.response?.data?.errors)
                    .flatMap((errorArray) => errorArray)
                    .forEach((error) => toast.warn(error));
                }
              });
            }
          }}
        />

        {/* Delete Modal */}
        {isDeleteModalOpen && (
          <DeleteConfirmModal
            isOpen
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={handleConfirmDelete}
            itemName={selectedUser?.name}
            language={language}
          />
        )}
      </main>
    </div>
  );
};

export default UsersManagementHub;
